import { useQuery } from "react-query";
import { AxiosError } from "axios";
import _compact from "lodash/compact";
import { useErrorBoundary } from "component-library";
import { authAxios as axios } from "shared-auth";
import { HALManager, UserManager } from "core";
import { useGetPublicProfile } from "pages/Profile/hooks/useGetPublicProfile";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { getApiUrl } from "services/UrlBuilder";
import Photo from "../../../models/Photos/Photo";

export const mainPhotoQueryKey = "mainPhotoQueryKey";

export const galleryQueryKey = "galleryQueryKey";

export function useGetGallery() {
  const captureError = useErrorBoundary();
  const { isPublicView } = usePublicView();
  const { data: publicProfileData, isLoading: isPublicProfileLoading } =
    useGetPublicProfile();

  const {
    data: mainPhotoData = {},
    isLoading: mainPhotoLoading,
    isError: isMainPhotoError,
  } = useQuery<Photo, AxiosError>(
    mainPhotoQueryKey,
    async () => {
      const { data } = await axios.get(
        getApiUrl(HALManager.getMainPhotoByProfileIdUrl(UserManager.userId))
      );

      return data;
    },
    {
      enabled: !isPublicView,
      cacheTime: 0,
      onError: captureError,
    }
  );

  const {
    data: photosData = {},
    isLoading: photosIsLoading,
    isError: isPhotosError,
  } = useQuery(
    galleryQueryKey,
    async () => {
      const { data } = await axios.get(
        getApiUrl(HALManager.getGalleryByProfileIdUrl(UserManager.userId))
      );

      return data.photos;
    },
    { enabled: !isPublicView, cacheTime: 0, onError: captureError }
  );

  const isLoading =
    isPublicProfileLoading || mainPhotoLoading || photosIsLoading;
  const isError = isMainPhotoError || isPhotosError;

  if (isError) return { gallery: [] };

  if (isLoading) {
    return {
      isLoading,
      gallery: [],
    };
  }

  const mainPhoto = isPublicView
    ? {
        ...publicProfileData?.mainPhoto,
        url: publicProfileData?.mainPhoto?.fileName,
      }
    : mainPhotoData;
  const photos = isPublicView
    ? publicProfileData?.photos?.map((photo) => ({
        ...photo,
        url: photo?.fileName,
      }))
    : photosData?.filter((photo) => !photo.isHidden);

  const gallery = _compact([mainPhoto, ...(photos || [])]);

  return {
    gallery,
  };
}
